import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ClipboardList, History, Loader2, Euro, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDate, eur } from "@/lib/format";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

export default function Dashboard() {
  const [pending, setPending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, r, m] = await Promise.all([
        api.get("/jobs/pending"),
        api.get("/jobs/recent"),
        api.get("/dashboard/monthly"),
      ]);
      setPending(p.data);
      setRecent(r.data);
      setMonthly(m.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markDone = async (job) => {
    setBusy(job.id);
    try {
      await api.post(`/jobs/${job.id}/done`);
      toast.success("Trabajo marcado como realizado");
      await load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Inicio</h1>
        <p className="text-muted-foreground mt-1">Resumen de tu actividad</p>
      </div>

      {/* Billing summary */}
      {monthly && (
        <section data-testid="monthly-summary" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-lg bg-[#0b1a3a] text-white p-5 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-white/60">Facturación de {MONTHS[parseInt(monthly.month.split("-")[1], 10) - 1]}</p>
              <p className="font-display text-3xl font-bold mt-1" data-testid="monthly-total">{eur(monthly.total)}</p>
              <p className="text-white/60 text-sm mt-1">{monthly.count} trabajo{monthly.count === 1 ? "" : "s"} este mes</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <Euro className="h-6 w-6 text-primary" />
            </div>
          </div>
          <div className="rounded-lg bg-primary text-primary-foreground p-5 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-primary-foreground/70">Facturación total (histórico)</p>
              <p className="font-display text-3xl font-bold mt-1" data-testid="alltime-total">{eur(monthly.all_total || 0)}</p>
              <p className="text-primary-foreground/70 text-sm mt-1">{monthly.all_count || 0} trabajo{(monthly.all_count === 1) ? "" : "s"} en total</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
          </div>
        </section>
      )}

      {/* Pending jobs */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Trabajos pendientes</h2>
          <span className="ml-1 text-sm font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5"
            data-testid="pending-count">{pending.length}</span>
        </div>
        {pending.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-muted-foreground">
            No tienes trabajos pendientes. ¡Todo al día!
          </div>
        ) : (
          <div className="space-y-2" data-testid="pending-list">
            {pending.map((j) => (
              <div key={j.id} data-testid={`pending-item-${j.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-4">
                <div className="min-w-0">
                  <p className="font-medium truncate">{j.title}</p>
                  <p className="text-sm text-muted-foreground truncate">{j.client_name}</p>
                </div>
                <Button size="sm" onClick={() => markDone(j)} disabled={busy === j.id}
                  data-testid={`mark-done-${j.id}`} className="shrink-0">
                  {busy === j.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  <span className="ml-1.5 hidden sm:inline">Realizado</span>
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent completed */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Últimos trabajos realizados</h2>
        </div>
        {recent.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-muted-foreground">
            Aún no hay trabajos realizados.
          </div>
        ) : (
          <div className="space-y-2" data-testid="recent-list">
            {recent.map((j) => (
              <div key={j.id} data-testid={`recent-item-${j.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-4">
                <div className="min-w-0">
                  <p className="font-medium truncate">{j.title}</p>
                  <p className="text-sm text-muted-foreground truncate">{j.client_name}</p>
                </div>
                <span className="text-sm text-muted-foreground shrink-0">{fmtDate(j.completed_date)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
